import type { CurrentCart, LanguageIsoCode } from '@/graphql/graphql';
import { ADD_TO_CART, GET_CART, REMOVE_FROM_CART, UPDATE_CART } from '@/graphql/queries/cart.graphql';
import { ApolloError } from '@apollo/client';
import { apolloClient } from './apollo-client';

export const getCart = async (): Promise<CurrentCart> => {
    const response = await apolloClient.query({
        query: GET_CART,
    });

    return response.data.cart;
};

export const addToCart = async (
    programId: string,
    language: LanguageIsoCode,
    excludeEpisodeIds?: string[],
): Promise<CurrentCart> => {
    try {
        const { data } = await apolloClient.mutate({
            mutation: ADD_TO_CART,
            variables: {
                input: {
                    programId,
                    language,
                    ...(excludeEpisodeIds ? { excludeEpisodeIds } : {}),
                },
            },
        });

        if (!data) throw new Error('addToCart mutation returned no data');

        return data.cart.billing.addToCart;
    } catch (error) {
        if (error instanceof ApolloError && error.graphQLErrors[0]?.message) {
            throw new Error(error.graphQLErrors[0].message);
        }
        if (error instanceof Error && error.message) {
            throw new Error(error.message);
        }
        throw new Error('Unable to add program to cart');
    }
};

export const removeFromCart = async (programId: string, language: LanguageIsoCode): Promise<CurrentCart> => {
    const { data } = await apolloClient.mutate({
        mutation: REMOVE_FROM_CART,
        variables: {
            input: { programId, language },
        },
    });

    if (!data) throw new Error('removeFromCart mutation returned no data');
    return data.cart.billing.removeFromCart;
};

export const updateCart = async (
    programId: string,
    currentLanguage: LanguageIsoCode,
    newLanguage: LanguageIsoCode,
): Promise<CurrentCart> => {
    const { data } = await apolloClient.mutate({
        mutation: UPDATE_CART,
        variables: {
            input: {
                programId,
                currentLanguage,
                newLanguage,
            },
        },
    });

    if (!data) throw new Error('updateCart mutation returned no data');
    return data.cart.billing.updateCart;
};
